import React, { useRef, useState } from "react";
import { Alert, Chip, LinearProgress, Skeleton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FaRegFileImage, FaUpload } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import AppButton from "../../../shared/AppButton";
import AppTable from "../../../shared/AppTable";
import Pagination from "../../../components/Pagination";
import TableSkeletonLoader from "../../../common/TableLoading";
import StatusOutcomePill from "../../../shared/StatusOutcomePill";
import { importCanResume, importCanStop, importError, importIsActive, uploadImportWorkbook } from "../ImportJobs";

const PROGRESS_SX = {
  height: 6,
  borderRadius: 999,
  backgroundColor: "#e2e8f0",
  "& .MuiLinearProgress-bar": { backgroundColor: "#0070C2", borderRadius: 999 },
};

const OUTLINED_BUTTON = "!border-gray-300 !text-gray-600";

const PRIMARY_OUTLINED_BUTTON = "!border-[#2441c9] !text-[#2441c9] hover:!bg-primary/10";

const ELIGIBILITY_STATUS_CHIP_SX = {
  eligible: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  "not-active": {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
  },
  "out-network": {
    backgroundColor: "#fef3c7",
    color: "#92400e",
  },
};

function EligibilityStatusChip({ status }) {
  const normalizedStatus = String(status).trim().toLowerCase().replace(/[_\s]+/g, "-");

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.6875rem",
        ...ELIGIBILITY_STATUS_CHIP_SX[normalizedStatus],
      }}
    />
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-base font-semibold text-slate-800">{value ?? "—"}</p>
    </div>
  );
}

export function ImportUploadForm({
  jobType,
  uploadHint,
  onDownloadSample,
  sampleLabel,
  onUploaded,
  onBusyChange,
  disabled = false,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  function setBusy(value) {
    onBusyChange?.(value);
  }

  function clearFile() {
    if (uploading) return;
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) setFile(droppedFile);
  }

  async function submit(event) {
    event.preventDefault();
    if (!file || !file.name.toLowerCase().endsWith(".xlsx")) {
      setError(t("Select an .xlsx file."));
      return;
    }
    if (file.size > 1024 * 1024 * 1024) {
      setError(t("The maximum request size is 1 GiB. Split large workbooks."));
      return;
    }
    setUploading(true);
    setBusy(true);
    setUploadPercent(null);
    setError("");
    try {
      const data = await uploadImportWorkbook(file, {
        type: jobType,
        onProgress: (progressEvent) =>
          setUploadPercent(progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : null),
      });
      if (!data.job_id) throw new Error("The server did not return a job ID. Deploy the background import API first.");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onUploaded?.(data.job_id);
    } catch (e) {
      setError(importError(e) + " " + t("If the connection was interrupted, check Import Jobs before uploading again to avoid duplicates."));
    } finally {
      setUploading(false);
      setBusy(false);
      setUploadPercent(null);
    }
  }

  async function downloadSample() {
    if (!onDownloadSample) return;
    setSampleLoading(true);
    setBusy(true);
    setError("");
    try {
      await onDownloadSample();
    } catch (e) {
      setError(importError(e));
    } finally {
      setSampleLoading(false);
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[12px] text-slate-600">
        {uploadHint || t("Upload an .xlsx workbook. Processing continues in the background after the file is saved, even if you close this window.")}
      </p>
      <div
        className={`mt-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 transition-colors ${
          isDragging ? "border-[#0070C2] bg-[#0070C2]/5" : "border-gray-300"
        }`}
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
      >
        <input
          ref={fileInputRef}
          aria-label={t("Excel workbook")}
          type="file"
          accept=".xlsx"
          hidden
          disabled={uploading}
          onChange={(event) => setFile(event.target.files?.[0] || null)}
        />
        <AppButton
          label={t("browse_file")}
          variant="outlined"
          startIcon={<FaUpload size={12} />}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className={PRIMARY_OUTLINED_BUTTON}
        />
        {file ? (
          <span className="flex min-w-0 items-center gap-1.5 rounded-[4px] bg-slate-100 px-2 py-[5px] text-[12px] text-slate-700">
            <FaRegFileImage size={13} className="shrink-0 text-[#0070C2]" />
            <span className="max-w-[240px] truncate">{file.name}</span>
            <DeleteOutlineIcon
              aria-label={t("Remove file")}
              onClick={clearFile}
              sx={{ fontSize: 16, color: uploading ? "#9ca3af" : "#ef4444", cursor: uploading ? "default" : "pointer" }}
            />
          </span>
        ) : (
          <span className="text-[12px] text-gray-400">{t("please_select_file_to_upload")}</span>
        )}
        <AppButton
          type="submit"
          label={t("Upload Excel")}
          startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
          isLoading={uploading}
          loadingText={t("Uploading…")}
          disabled={disabled || uploading || sampleLoading || !file}
        />
        {onDownloadSample && (
          <AppButton
            label={sampleLabel || t("download_sample")}
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 17 }} />}
            onClick={downloadSample}
            isLoading={sampleLoading}
            loadingText={t("Downloading…")}
            disabled={disabled || uploading}
            className={OUTLINED_BUTTON}
          />
        )}
      </div>
      {uploading && (
        <div className="mt-2" role="status">
          <LinearProgress variant={uploadPercent === null ? "indeterminate" : "determinate"} value={uploadPercent || 0} sx={PROGRESS_SX} />
          <p className="mt-1 text-[11px] text-slate-500">
            {uploadPercent === 100
              ? t("File transferred; waiting for the server to save and queue it…")
              : t("File upload") + (uploadPercent === null ? "" : ` ${uploadPercent}%`)}
          </p>
        </div>
      )}
      {error && <Alert severity="error" className="mt-3">{error}</Alert>}
    </form>
  );
}

export function ImportJobsTable({
  jobs = [],
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  onView,
  onRefresh,
  disabled = false,
  title,
}) {
  const { t } = useTranslation();

  const columns = [
    { header: t("Submitted"), render: (row) => new Date(row.created_at).toLocaleString() },
    { header: t("Status"), render: (row) => <StatusOutcomePill value={row.status} /> },
    { header: t("Processed"), align: "center", render: (row) => Number(row.processed).toLocaleString() },
    { header: t("Succeeded"), align: "center", render: (row) => Number(row.succeeded).toLocaleString() },
    {
      header: t("Failed"),
      align: "center",
      render: (row) => (
        <span className={Number(row.failed) > 0 ? "font-semibold text-red-600" : undefined}>
          {Number(row.failed).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <>
      {error && <Alert severity="error" className="mt-3">{error}</Alert>}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-[13px] font-bold text-slate-800">{title || t("Today’s import jobs")}</h3>
        <AppButton
          label={t("Refresh")}
          variant="outlined"
          startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
          onClick={onRefresh}
          isLoading={loading}
          loadingText={t("Refreshing…")}
          disabled={disabled}
          className={OUTLINED_BUTTON}
        />
      </div>

      <AppTable
        columns={columns}
        data={jobs}
        loading={Boolean(loading) && jobs.length === 0}
        LoaderComponent={<TableSkeletonLoader columnCount={columns.length + 1} rowCount={5} />}
        onView={onView}
      />

      <div className="mt-2">
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </>
  );
}

export function ImportJobDetailView({ jobId, detail, footer }) {
  const { t } = useTranslation();
  const {
    job,
    jobStatus,
    detailLoading,
    detailError,
    results,
    resultsLoading,
    resultsError,
    errorsOnly,
    setErrorsOnly,
    resultsPage,
    resultPages,
    changeResultsPage,
    busy,
    stopping,
    resuming,
    downloading,
    secondsLeft,
    refresh,
    stop,
    resume,
    downloadIssues,
  } = detail;

  const refreshLabel = secondsLeft === null
    ? t("Refresh")
    : `${t("Refresh")} (${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")})`;

  const resultColumns = [
    // { header: t("Sheet"), align: "center", render: (row) => Number(row.sheet_index ?? 0) + 1 },
    // { header: t("Row"), align: "center", render: (row) => row.row },
    {
      header: t("Document ID"),
      render: (row) => <span className="font-mono text-[11px]">{row.document_id || "—"}</span>,
    },
    {
      header: t("Patient"),
      render: (row) => <span className="font-mono text-[11px]">{row.patient_name || "—"}</span>,
      cellSx: { whiteSpace: "normal", wordBreak: "break-all", maxWidth: "180px" },
    },
    // {
    //   header: t("Patient ID"),
    //   render: (row) => <span className="font-mono text-[11px]">{row.patient_id || "—"}</span>,
    //   cellSx: { whiteSpace: "normal", wordBreak: "break-all", maxWidth: "160px" },
    // },
    {
      header: t("Eligibility Status"),
      render: (row) => row.eligibility_status
        ? <EligibilityStatusChip status={row.eligibility_status} />
        : "—",
      cellSx: { whiteSpace: "normal", wordBreak: "break-all", maxWidth: "160px" },
    },
    {
      header: t("Result"),
      render: (row) => row.error
        ? <span className="font-medium text-red-600">{row.error}</span>
        : <span className="font-medium text-green-700">{t("Succeeded")}</span>,
      cellSx: { whiteSpace: "normal", minWidth: "240px" },
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-slate-500">
          {t("Job ID")}: <span className="font-mono break-all">{jobId}</span>
        </p>
        <AppButton
          label={refreshLabel}
          variant="outlined"
          startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
          onClick={refresh}
          isLoading={detailLoading}
          loadingText={t("Refreshing…")}
          disabled={busy || !importIsActive(jobStatus)}
          className={OUTLINED_BUTTON}
        />
      </div>

      {detailError && <Alert severity="error" className="mt-2">{detailError}</Alert>}
      {!job && !detailError && (
        <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4" role="status" aria-label={t("Loading progress…")}>
          {[0, 1, 2, 3].map((key) => <Skeleton key={key} variant="rounded" height={62} />)}
        </div>
      )}

      {job && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4" aria-live="polite">
            <SummaryCard label={t("Status")} value={<StatusOutcomePill value={job.status} />} />
            <SummaryCard label={t("Processed")} value={Number(job.processed).toLocaleString()} />
            <SummaryCard label={t("Succeeded")} value={Number(job.succeeded).toLocaleString()} />
            <SummaryCard label={t("Failed")} value={Number(job.failed).toLocaleString()} />
          </div>

          {importIsActive(job.status) && <LinearProgress className="mt-2" sx={PROGRESS_SX} />}
          {job.error && <Alert severity="error" className="mt-2">{job.error}</Alert>}

          {importCanStop(job.status) && (
            <div className="mt-3">
              <AppButton
                label={t("Stop import")}
                bgColor="#dc2626"
                startIcon={<StopCircleOutlinedIcon sx={{ fontSize: 17 }} />}
                onClick={stop}
                isLoading={stopping}
                loadingText={t("Stopping…")}
                disabled={busy}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                {t("The worker will stop after the current operation and preserve the last committed checkpoint. You can resume this job later.")}
              </p>
            </div>
          )}

          {importCanResume(job.status) && (
            <div className="mt-3">
              <AppButton
                label={t("Resume from checkpoint")}
                startIcon={<ReplayOutlinedIcon sx={{ fontSize: 17 }} />}
                onClick={resume}
                isLoading={resuming}
                loadingText={t("Resuming…")}
                disabled={busy}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                {t("Fix the job-level issue first. Resume preserves previous results and continues after the last committed row. A row interrupted before its checkpoint may be repeated.")}
              </p>
            </div>
          )}

          {job.status === "completed_with_errors" && (
            <Alert severity="warning" className="mt-2">
              {t("Processing finished. Correct failed rows and upload only those rows as a new workbook. Resume does not retry individual row errors.")}
            </Alert>
          )}
        </>
      )}

      <div className="flex !w-full justify-between gap-2">
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <label className="flex w-max cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(event) => setErrorsOnly(event.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 accent-[#0070C2] focus:ring-0"
            />
            <span className="text-[12px] font-semibold text-gray-700">{t("Show errors only")}</span>
          </label>
        </div>
        {Number(job?.failed) > 0 && (
          <div className="my-2">
            <AppButton
              label={t("Download issues Excel")}
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 17 }} />}
              onClick={downloadIssues}
              isLoading={downloading}
              loadingText={t("Downloading issues…")}
              disabled={busy}
              className={OUTLINED_BUTTON}
            />
          </div>
        )}
      </div>

      {resultsError && <Alert severity="error" className="mt-1">{resultsError}</Alert>}
      <AppTable
        columns={resultColumns}
        data={results}
        loading={resultsLoading && results.length === 0}
        LoaderComponent={<TableSkeletonLoader columnCount={resultColumns.length} rowCount={5} />}
        actions={false}
      />

      <div className="mt-2">
        <Pagination currentPage={resultsPage} totalPages={resultPages} onPageChange={changeResultsPage} />
      </div>

      {footer}
    </>
  );
}
